import {createSelector} from '@reduxjs/toolkit'

import {first} from '@shared/helpers/array'
import {groupBy, includes} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'
import {mapValueKeys} from '@shared/helpers/object'

import {
    CustomOrders,
    DeliveryTagTypeValues,
    ExceptionProductTypesByStoreCoupon,
    PRODUCTS_ECOUPON_LINKED_STATE,
    ProductClass,
} from '@web/core/constants'
import {isArrivalGuarantee, isKurlyOrder} from '@web/helpers/policies'

import {RootState, OrderSheetProduct} from './interfaces'

/** 주문서 상품들 */
const orderSheetProductsSelector = (state: RootState) => state.orderSheetProducts

type OrderAmountInfo = {
    orderAmount: number
    quantity: number
    productQuantity: number
    orderAmountByCoupon: number
    orderAmountByStoreCoupon: number
}
export type OrderSheetProductView = OrderSheetProduct & OrderAmountInfo

type OrderPriceInfo = {
    /** 단가 */
    unitPrice: number
    /** 옵션가 */
    optionPrice: number
    /** 수량 */
    quantity: number
    /** 상품 금액 */
    orderAmount: number
    /** 복수 구매 할인 금액 */
    multipleDiscountAmount: number
    /** 판매자 No */
    merchantNo: number
}

export type CouponOrderSheetItemFlatMapType = {[OrderSheetItemId: string]: OrderPriceInfo}

const getProductEcouponLinkedState = (productCount: number, eCouponLinkedProductCount: number) => {
    if (eCouponLinkedProductCount === 0) {
        return PRODUCTS_ECOUPON_LINKED_STATE.NONE
    } else if (eCouponLinkedProductCount === productCount) {
        return PRODUCTS_ECOUPON_LINKED_STATE.ALL
    }
    return PRODUCTS_ECOUPON_LINKED_STATE.MIXED
}

/** 주문서 상품 목록 정보 */
export const orderSheetProductInfoSelector = createSelector(orderSheetProductsSelector, (orderSheetProducts) => {
    const products: OrderSheetProductView[] = orderSheetProducts.map((product) => {
        const {items, quantity: originQuantity, orderAmount: originOrderAmount} = product

        const {orderAmount, quantity, productQuantity, orderAmountByCoupon, orderAmountByStoreCoupon} =
            items.reduce<OrderAmountInfo>(
                (acc, next) => {
                    const eqProductClassType = eq(next.productClassType)
                    if (next.orderAmount) {
                        acc.orderAmount += next.orderAmount
                    }
                    // 스토어 쿠폰은 전통주와 도서의 계산에 포함되지 않습니다.
                    if (!ExceptionProductTypesByStoreCoupon.includes(product.type)) {
                        acc.orderAmountByStoreCoupon += next.orderAmount ?? 0
                    }
                    if (!eqProductClassType(ProductClass.SUPPLEMENTED)) {
                        // 추가상품은 전체 수량에 포함되지 않는다. (쿠폰을 위한 수량)
                        acc.quantity += next.quantity
                        acc.orderAmountByCoupon += next.orderAmount ?? 0
                    }
                    acc.productQuantity += next.quantity
                    return acc
                },
                {orderAmount: 0, quantity: 0, productQuantity: 0, orderAmountByCoupon: 0, orderAmountByStoreCoupon: 0},
            )
        return {
            ...product,
            orderAmount: originOrderAmount ?? orderAmount,
            orderAmountByCoupon: originOrderAmount ?? orderAmountByCoupon,
            orderAmountByStoreCoupon: originOrderAmount ?? orderAmountByStoreCoupon,
            quantity: originQuantity ?? quantity,
            productQuantity,
        }
    })

    const productsByDeliveryGroup: {
        [deliveryGroupKey: string]: OrderSheetProductView[]
    } = groupBy('deliveryInfo.deliveryGroupKey', products)
    const productsByMerchantNo: {
        [merchantNo: number]: OrderSheetProductView[]
    } = groupBy('merchantInfo.merchantNo', products)
    const productsByMerchantProductId: {
        [merchantProductId: string]: OrderSheetProductView[]
    } = groupBy('merchantInfo.merchantProductId', products)
    const hasArrivalGuaranteeProduct: boolean = products.some(({deliveryInfo: {deliveryAttributeType}}) =>
        isArrivalGuarantee(deliveryAttributeType),
    )
    const hasKurlyProduct: boolean = products.some(({deliveryInfo}) => {
        return isKurlyOrder({branchDeliveryType: deliveryInfo.branch?.branchDeliveryType})
    })
    const productById = products.reduce<{
        [id: string]: OrderSheetProductView
    }>((acc, product) => {
        return {
            ...acc,
            [product.id]: product,
        }
    }, {})

    return {
        /** 상품 목록 */
        products,
        /** 상품 개수 */
        productSize: products.length,
        /** 상품 item 개수 */
        productItemSize: products.reduce((acc, {items}) => {
            return acc + items.length
        }, 0),
        /** 상품 총 개수 */
        productQuantity: products.reduce((acc, {productQuantity}) => {
            return acc + productQuantity
        }, 0),
        /** 맞춤제작 상품 포함 여부 */
        hasCustomOrder: products.some(({customOrderDetailType}) => includes(customOrderDetailType, CustomOrders)),
        /** 예약구매 상품 포함 여부 */
        hasPreOrderProduct: products.some(({preOrder}) => !!preOrder),
        /** 도착보장 상품 포함 여부 */
        hasArrivalGuaranteeProduct,
        /** 컬리 상품 포함 여부 */
        hasKurlyProduct,
        /** 배송지 group 별 상품 목록 */
        productsByDeliveryGroup,
        /** 판매자 별 상품 목록 */
        productsByMerchantNo,
        /** 판매자 상품별 목록 */
        productsByMerchantProductId,
        /** 판매자 별 orderSheetItemId 목록 */
        orderSheetItemIdByMerchantNo: mapValueKeys((productViews) => {
            return productViews.reduce<string[]>(
                (acc, {items}) => [...acc, ...items.map(({orderSheetItemId}) => orderSheetItemId)],
                [],
            )
        }, productsByMerchantNo),
        /** 상품 id 별 상품 정보 */
        productById,
        /** 총 상품 금액 */
        totalOrderAmount: products.reduce((acc, {orderAmount}) => {
            return acc + orderAmount
        }, 0),
        /** 상품 Item 별 단가 정보 */
        orderSheetItemFlatMap: products.reduce<CouponOrderSheetItemFlatMapType>(
            (acc, {items, merchantInfo, unitPrice}) => {
                return {
                    ...acc,
                    ...items.reduce<CouponOrderSheetItemFlatMapType>((itemsAcc, item) => {
                        if (item.orderSheetItemId) {
                            return {
                                ...itemsAcc,
                                [item.orderSheetItemId]: (function () {
                                    return {
                                        orderAmount: item.orderAmount,
                                        unitPrice,
                                        optionPrice: item.price,
                                        quantity: item.quantity,
                                        multipleDiscountAmount: 0,
                                        merchantNo: merchantInfo.merchantNo,
                                    }
                                })(),
                            }
                        }
                        return itemsAcc
                    }, {}),
                }
            },
            {},
        ),
        productsEcouponLinkedState: getProductEcouponLinkedState(
            products.length,
            products.filter(({ecouponBarcodeConnect}) => ecouponBarcodeConnect).length,
        ),
        deliveryTagTypes: products.reduce((acc, product) => {
            const deliveryTagType = product.deliveryInfo.deliveryTagType

            if (deliveryTagType) {
                return [...acc, deliveryTagType]
            }

            return acc
        }, [] as DeliveryTagTypeValues[]),
    }
})

/** 도착 보장 정보 selector */
export const arrivalGuaranteeInfoSelector = createSelector(orderSheetProductsSelector, (products) => {
    const hasArrivalGuaranteeProduct: boolean = products.some(({deliveryInfo: {deliveryAttributeType}}) =>
        isArrivalGuarantee(deliveryAttributeType),
    )
    const arrivalGuaranteesByOrderSheetProductId = products.reduce<
        Record<
            string,
            {
                arrivalGuaranteeDateTime: string
                rmid: Maybe<string>
                outboundLocationId: Maybe<string>
                deliveryTagType?: DeliveryTagTypeValues
            }
        >
    >((acc, product) => {
        const {
            arrivalGuaranteeDateTime,
            rmid,
            outboundLocationId,
            id,
            deliveryInfo: {deliveryTagType},
        } = product

        if (arrivalGuaranteeDateTime) {
            return {
                ...acc,
                [id]: {
                    arrivalGuaranteeDateTime,
                    rmid,
                    outboundLocationId,
                    deliveryTagType,
                },
            }
        }

        return acc
    }, {})
    return {
        hasArrivalGuaranteeProduct,
        arrivalGuaranteesByOrderSheetProductId,
    }
})

/** 상품목록 > 대표 상품 정보 */
export const primaryProductSelector = createSelector(orderSheetProductsSelector, (products) => {
    const primaryProduct = first(products)

    return primaryProduct
})

/** 외부 장보기 배송 정보 */
export const externalBranchDeliverySelector = createSelector(primaryProductSelector, (product) => {
    const isKurlyProduct = isKurlyOrder({branchDeliveryType: product?.deliveryInfo.branch?.branchDeliveryType})
    const {deliveryScheduleType, branchDeliveryDate, branchDeliveryEndTime} = product?.deliveryInfo.branch ?? {}

    // 현재는 외부 장보기 배송 정보 속성을 가진 상품은 컬리 상품만 존재함
    const hasExternalBranchDeliveryProduct = isKurlyProduct
    if (!hasExternalBranchDeliveryProduct) {
        return
    }

    if (!deliveryScheduleType || !branchDeliveryDate || !branchDeliveryEndTime) {
        return undefined
    }

    const rawFormattedBranchDeliveryDate = branchDeliveryDate.split('-').join('')
    const rawFormattedBranchDeliveryEndTime = branchDeliveryEndTime.split(':').join('')
    const isFormatValid = !!rawFormattedBranchDeliveryDate && rawFormattedBranchDeliveryEndTime.length === 4

    return {
        deliveryScheduleType,
        branchDeliveryDate,
        branchDeliveryEndTime,
        ...(isFormatValid && {
            /** law format(BE응답) 으로 변환된 날짜, 시간 */
            lawFormatted: {
                branchDeliveryDate: rawFormattedBranchDeliveryDate,
                branchDeliveryEndTime: rawFormattedBranchDeliveryEndTime,
            },
        }),
    }
})
